

import { createContext, useContext, useState, useMemo, useCallback, useRef } from "react";
import { GuestLog } from "../types/GuestLog";
import {
  createGuestNote,
  getGuestLogs,
  updateGuestNote,
} from "@/lib/api/handlers/GuestLogHandling";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";
import { GuestLogType } from "../types/enums/GuestLogType";
import { parseString } from "@/lib/utils/parser";
import { isValidObjectId } from "@/lib/utils/input-validator";

type GuestLogsMap = Map<string, GuestLog[] | null>;

type GuestLogsContextType = {
  allLogs: GuestLogsMap;
  /**
   * READ errors, keyed by the guestId whose fetch failed.
   *
   * Was a single global string, which worked only while exactly one timeline
   * ever loaded. The merged timeline fetches the host AND every companion, and
   * `fetchLogs` clears the error at the START of each call — so a companion's
   * request reliably erased the host's failure and an outage rendered as
   * "no history". Keyed, that cannot happen, and a stale error can no longer
   * surface on a different guest that was pre-cached by someone else's merge.
   *
   * WRITE errors are deliberately NOT in here: `addLog` / `updateNote` throw,
   * so the composer and the note editor can react where the user is looking.
   */
  loadErrors: Map<string, string>;
  fetchLogs: (guestId: string, refetch?: boolean) => Promise<void>;
  addLog: (guestId: string, note: string) => Promise<void>;
  updateNote: (
    guestId: string,
    guestLogId: string,
    note: string
  ) => Promise<void>;
};

const GuestLogsContext = createContext<GuestLogsContextType | undefined>(
  undefined
);

export const GuestLogsProvider = ({
  children,
  eventId,
}: {
  children: React.ReactNode;
  eventId: string;
}) => {
  // Initialize with a Map instead of an array
  const [allLogsState, setAllLogsState] = useState<GuestLogsMap>(new Map());
  const [loadErrors, setLoadErrors] = useState<Map<string, string>>(new Map());

  // `loading` used to live here as one global boolean. It is gone: every
  // fetchLogs ended in `finally { setLoading(false) }`, so with N+1 timelines
  // in flight the first one home switched the spinner off for all of them.
  // Consumers derive readiness from `allLogs.has(guestId)` instead.
  const setLoadError = useCallback((guestId: string, message: string | null) => {
    setLoadErrors((prev) => {
      if (message === null && !prev.has(guestId)) return prev;
      const next = new Map(prev);
      if (message === null) next.delete(guestId);
      else next.set(guestId, message);
      return next;
    });
  }, []);

  // Tracks which guestIds we've already fetched. Lives in a ref (not the state
  // Map) so `fetchLogs` can dedupe without depending on `allLogsState` — that
  // dependency made the callback a fresh identity on every log mutation, which
  // re-fired the consumer's `useEffect(..., [fetchLogs])` on each change.
  const loadedRef = useRef<Set<string>>(new Set());

  const fetchLogs = useCallback(async (guestId: string, refetch?: boolean) => {
    if (loadedRef.current.has(guestId) && !refetch) {
      return;
    }

    setLoadError(guestId, null);

    try {
      const logs = await getGuestLogs(eventId, guestId);

      if ("isServerError" in logs && logs.isServerError === true)
        throwServerError(logs);

      loadedRef.current.add(guestId);
      // Create a new Map with the updated data to ensure state change detection
      setAllLogsState((prev) => {
        const newMap = new Map(prev);
        newMap.set(guestId, logs as GuestLog[]);
        return newMap;
      });
    } catch (error) {
      let message = "unknown";
      if (error instanceof Error) message = error.message;
      setLoadError(guestId, message);
      setAllLogsState((prev) => {
        const newMap = new Map(prev);
        newMap.set(guestId, []);
        return newMap;
      });
    }
  }, [eventId, setLoadError]);

  const addLog = useCallback(async (guestId: string, note: string) => {
    try {
      let response = await createGuestNote(eventId, guestId, note);

      if ("isServerError" in response && response.isServerError === true)
        throwServerError(response);

      response = response as GuestLog;

      const log: GuestLog = {
        guestLogId: response.guestLogId,
        guestId: guestId,
        field: note,
        oldValue: "",
        newValue: "",
        createdAt: new Date(response.createdAt),
        type: GuestLogType.NOTE,
      };

      setAllLogsState((prev) => {
        const newMap = new Map(prev);
        const existingLogs = newMap.get(guestId) || [];
        newMap.set(guestId, [log, ...existingLogs]);
        return newMap;
      });
    } catch (error) {
      // Rethrow. Swallowing this made a failed save indistinguishable from a
      // successful one: the composer closed and cleared the typed note while
      // the only trace of the error was a global field the timeline no longer
      // renders. The caller owns the message now.
      throw error instanceof Error ? error : new Error("unknown");
    }
  }, [eventId]);

  const updateNote = useCallback(async (
    guestId: string,
    guestLogId: string,
    note: string
  ) => {
    try {
      const parsedString = parseString(note.trim(), "");
      if (!isValidObjectId(eventId)) throw new Error("validationError");
      if (!isValidObjectId(guestLogId)) throw new Error("validationError");
      if (!parsedString) throw new Error("validationError");

      const response = await updateGuestNote(eventId, guestLogId, parsedString);

      // The handler RESOLVES with a ServerError on 403/500 — it never rejects
      // (`GuestLogHandling.updateGuestNote` ends in
      // `catch { return handleErrorServer(error) }`). Without this guard the
      // rethrow below was unreachable for every real server failure, the
      // optimistic cache write below still ran, and `handleSave` took its
      // success path: editor closed, new text on screen, old value in the
      // database. `fetchLogs` and `addLog` have carried this guard all along.
      if (response && "isServerError" in response && response.isServerError === true)
        throwServerError(response);

      setAllLogsState((prev) => {
        const newMap = new Map(prev);
        const existingLogs = newMap.get(guestId) || [];

        // Create a new array with the updated log
        const updatedLogs = existingLogs.map((log) => {
          if (log.guestLogId === guestLogId) {
            // Create a new object for the updated log
            return { ...log, field: parsedString };
          }
          return log;
        });

        newMap.set(guestId, updatedLogs);
        return newMap;
      });
    } catch (error) {
      // Rethrow — see addLog. `handleSave` closed edit mode on a rejected save
      // and left the new text on screen, so a 403 looked exactly like success.
      throw error instanceof Error ? error : new Error("unknown");
    }
  }, [eventId]);

  const contextValue = useMemo(() => ({
    allLogs: allLogsState, loadErrors, fetchLogs, addLog, updateNote,
  }), [allLogsState, loadErrors, fetchLogs, addLog, updateNote]);

  return (
    <GuestLogsContext.Provider
      value={contextValue}
    >
      {children}
    </GuestLogsContext.Provider>
  );
};

export const useGuestLogs = () => {
  const context = useContext(GuestLogsContext);
  if (!context) {
    throw new Error("useGuestLogs must be used within a GuestLogsProvider");
  }
  return context;
};
